import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  type AutocompleteProps,
  type AutocompleteValue,
  Chip,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemText,
  type SxProps,
  type Theme,
  debounce,
} from '@mui/material';
import { Users } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  TeamsSelectFragment,
  Teams_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { RaAutoComplete } from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import { useRequestVersion } from '@modules/_shared/hooks/useRequestVersion';
import { useLocale } from '@shared/hooks/locale';

export const TEAMS_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment TeamsSelect on teams {
    id
    name
    logo {
      id
      url
    }
    team_images {
      image {
        url
      }
    }
  }
`);

const SEARCH_TEAMS = gql(/* GraphQL */ `
  query TeamsSelect($search: String!, $where: teams_bool_exp) {
    search_teams_tsv(args: { search_text: $search }, limit: 20, where: $where) {
      id
      ...TeamsSelect
    }
  }
`);

const GET_DEFAULT_TEAMS = gql(/* GraphQL */ `
  query GetDefaultTeams($ids: [uuid!]!) {
    teams(where: { id: { _in: $ids } }) {
      id
      ...TeamsSelect
    }
  }
`);

const LOAD_TEAMS = gql(/* GraphQL */ `
  query LoadTeams($where: teams_bool_exp) {
    teams(where: $where, limit: 20, order_by: { name: asc }) {
      id
      ...TeamsSelect
    }
  }
`);

interface TeamsSelectProps<Multiple extends boolean = false>
  extends Omit<
    AutocompleteProps<TeamsSelectFragment, Multiple, false, false>,
    'renderInput' | 'options' | 'onChange' | 'value'
  > {
  teamIds?: AutocompleteValue<string, Multiple, false, false>;
  label?: string;
  onChange?: (
    teamId: AutocompleteValue<string, Multiple, false, false>,
  ) => void;
  size?: 'small' | 'medium';
  excludeTeamsIds?: string[];
  sx?: SxProps<Theme>;
  InputProps?: InputProps;
  disabled?: boolean;
  loading?: boolean;
  openOnFocus?: boolean;
  fullWidth?: boolean;
  blurOnSelect?: boolean;
  clearOnBlur?: boolean;
  filters?: Teams_Bool_Exp;
}

export const TeamsSelect = <Multiple extends boolean = false>({
  onChange,
  InputProps,
  teamIds,
  excludeTeamsIds = [],
  sx,
  disabled = false,
  loading = false,
  openOnFocus = false,
  fullWidth = true,
  blurOnSelect = false,
  clearOnBlur = false,
  filters,
  multiple,
  autoFocus = false,
  onBlur,
  slotProps,
  disablePortal,
}: TeamsSelectProps<Multiple>) => {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = useState<TeamsSelectFragment[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const { startRequest, invalidateRequests, isCurrentRequest } =
    useRequestVersion();

  const [selectedValue, setSelectedValue] = useState<
    AutocompleteValue<
      TeamsSelectFragment & { isRestricted?: true },
      Multiple,
      false,
      false
    >
  >(
    (multiple ? [] : null) as AutocompleteValue<
      TeamsSelectFragment,
      Multiple,
      false,
      false
    >,
  );

  const { data: defaultTeams } = useQuery(GET_DEFAULT_TEAMS, {
    skip: !teamIds,
    variables: {
      ids: Array.isArray(teamIds) ? teamIds : teamIds == null ? [] : [teamIds],
    },
  });

  useEffect(() => {
    if (defaultTeams != null) {
      if (multiple) {
        setSelectedValue(
          (teamIds as string[]).map(tId => {
            const team = defaultTeams.teams?.find(t => t.id === tId);

            return team == null
              ? {
                  id: tId,
                  name: t('Restricted team'),
                  isRestricted: true,
                  logo: null,
                  team_images: [],
                }
              : team;
          }) as AutocompleteValue<
            TeamsSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      } else {
        const selectedTeam = defaultTeams.teams?.[0];

        setSelectedValue(
          (selectedTeam == null && teamIds != null
            ? {
                id: teamIds as string,
                name: t('Restricted team'),
                isRestricted: true,
                logo: null,
                team_images: [],
              }
            : selectedTeam) as AutocompleteValue<
            TeamsSelectFragment & { isRestricted?: true },
            Multiple,
            false,
            false
          >,
        );
      }
    }
  }, [defaultTeams, multiple, t, teamIds]);

  const [searchTeams, { loading: loadingTeams, error }] = useLazyQuery(
    SEARCH_TEAMS,
    { notifyOnNetworkStatusChange: true },
  );

  const teamsWhere = useMemo(
    () => ({
      id: { _nin: excludeTeamsIds },
      ...(filters ?? {}),
    }),
    [excludeTeamsIds, filters],
  );

  // Only fetch the team list once the dropdown is actually opened (and never
  // when disabled). This keeps `loading` reactive — so the menu shows a spinner
  // then teams instead of flashing "No results" — without firing a query on
  // mount for every TeamsSelect instance (e.g. read-only or per-row selects).
  const { data: initialTeams, loading: loadingInitialTeams } = useQuery(
    LOAD_TEAMS,
    { variables: { where: teamsWhere }, skip: disabled || !open },
  );

  // Seed the menu with the full team list whenever it changes and nothing is
  // being searched — so the open dropdown stays fresh as teamsWhere updates
  // (e.g. excludeTeamsIds growing). The !searchValue guard still prevents a
  // slower LOAD_TEAMS from clobbering active search results.
  useEffect(() => {
    if (open && !searchValue && initialTeams?.teams) {
      setOptions(initialTeams.teams);
    }
  }, [open, searchValue, initialTeams]);

  const showInitialTeams = useCallback(() => {
    setOpen(true);
    setOptions(initialTeams?.teams ?? []);
  }, [initialTeams]);

  const search = useCallback(
    (value: string) => {
      setSearchValue(value);
      const requestId = startRequest();
      if (!value) {
        showInitialTeams();
        return;
      }
      searchTeams({
        variables: {
          search: value,
          where: teamsWhere,
        },
      }).then(response => {
        if (!isCurrentRequest(requestId)) {
          return;
        }
        setOpen(true);
        setOptions(response.data?.search_teams_tsv ?? []);
      });
    },
    [searchTeams, teamsWhere, showInitialTeams, isCurrentRequest, startRequest],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        search(value);
      }, 275),
    [search],
  );

  const isRestricted =
    !multiple &&
    (selectedValue as { isRestricted?: true } | null)?.isRestricted;

  return (
    <RaAutoComplete
      sx={sx}
      slotProps={slotProps}
      disablePortal={disablePortal}
      disabled={disabled}
      autoFocus={autoFocus}
      multiple={multiple}
      open={open}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onClose={() => {
        setOpen(false);
        setOptions([]);
        setSearchValue('');
        invalidateRequests();
      }}
      onOpen={showInitialTeams}
      fullWidth={fullWidth}
      debouncedSearch={debouncedSearch}
      setOpen={setOpen}
      setOptions={setOptions}
      openOnFocus={openOnFocus}
      onBlur={onBlur}
      onFocus={() => {
        if (openOnFocus) {
          showInitialTeams();
        }
      }}
      clearOnBlur={clearOnBlur}
      blurOnSelect={blurOnSelect}
      value={selectedValue}
      getOptionLabel={option => option.name ?? ''}
      options={options}
      loading={loadingTeams || loadingInitialTeams || loading}
      loadingText={t('Loading...')}
      noOptionsText={error ? t('Error') : t('No results')}
      allowEmptySearch
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={Users} />
        ),
        placeholder: disabled
          ? t('No team')
          : InputProps?.placeholder ?? t('Search teams'),
      }}
      onChange={async (
        _: any,
        value: AutocompleteValue<TeamsSelectFragment, Multiple, false, false>,
      ) => {
        setSelectedValue(
          value as AutocompleteValue<
            TeamsSelectFragment,
            Multiple,
            false,
            false
          >,
        );
        if (Array.isArray(value)) {
          onChange?.(
            value.map(team => team.id) as AutocompleteValue<
              string,
              Multiple,
              false,
              false
            >,
          );
        } else {
          onChange?.(
            (value?.id ?? null) as AutocompleteValue<
              string,
              Multiple,
              false,
              false
            >,
          );
        }
      }}
      filterOptions={x => x}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemAvatar>
              <TeamAvatar
                logoUrl={
                  option.logo?.url ?? option.team_images?.[0]?.image?.url
                }
                size={40}
              />
            </ListItemAvatar>
            <ListItemText primary={option.name} />
          </ListItem>
        );
      }}
      renderValue={
        multiple
          ? (value, getItemProps) =>
              (value as (TeamsSelectFragment & { isRestricted?: true })[]).map(
                (option, index) => {
                  const { disabled: chipDisabled, ...tagProps } = getItemProps({
                    index,
                  });

                  return (
                    <Chip
                      sx={{ '& .MuiAvatar-root': { ml: 0.5, mr: -0.5 } }}
                      key={option.id}
                      size="small"
                      label={option.name}
                      {...tagProps}
                      disabled={chipDisabled || option.isRestricted}
                    />
                  );
                },
              )
          : undefined
      }
      tooltip={
        isRestricted ? t("You don't have access to this team") : undefined
      }
    />
  );
};
